import { Platform } from 'react-native';
import { firebaseConfig } from '../config/firebaseConfig';

// Web Firebase imports (lazy loaded)
let webAuth: any = null;
let webDb: any = null;
let webStorage: any = null;

// Mobile Firebase (lazy loaded - only for mobile, not web)
let mobileAuth: any = null;
let mobileFirestore: any = null;

// Initialize Firebase based on platform
const initializeFirebase = (): boolean => {
  if (Platform.OS === 'web') {
    // Web Firebase
    try {
      const { initializeApp, getApps } = require('firebase/app');
      const { getAuth } = require('firebase/auth');
      const { getFirestore } = require('firebase/firestore');
      const { getStorage } = require('firebase/storage');
      
      // Import config from centralized location
      const { firebaseConfig } = require('../config/firebaseConfig');
      
      // Check if Firebase is already initialized
      let app;
      const existingApps = getApps();
      if (existingApps.length > 0) {
        app = existingApps[0];
        console.log('✅ Using existing Firebase app instance');
      } else {
        // Validate config before initializing
        if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
          console.error('❌ Firebase config is missing required fields (apiKey, projectId)');
          console.error('💡 Create a .env file with REACT_APP_FIREBASE_* variables');
          console.error('💡 See FIREBASE_CONFIG_SETUP.md for instructions');
          console.error('Current config:', { 
            hasApiKey: !!firebaseConfig.apiKey, 
            hasProjectId: !!firebaseConfig.projectId 
          });
          return false;
        }
        app = initializeApp(firebaseConfig);
        console.log('✅ Firebase app initialized');
      }
      
      webAuth = getAuth(app);
      
      // Connect to default Firestore database (same as mobile)
      // IMPORTANT: Using default database so web and mobile share the same data
      // IMPORTANT: Only create Firestore instance if it doesn't exist
      // Creating multiple instances causes "INTERNAL ASSERTION FAILED" errors
      if (!webDb) {
        webDb = getFirestore(app); // Use default database (no second parameter)
        console.log('✅ Connected to Firestore database: (default)');
        console.log('✅ Web and mobile now use the same database for unified data');
      } else {
        console.log('✅ Reusing existing Firestore instance');
      }
      if (!webStorage && firebaseConfig.storageBucket) {
        webStorage = getStorage(app);
        console.log('✅ Connected to Firebase Storage');
      }
      
      if (!webAuth) {
        console.error('❌ Failed to get Firebase Auth instance');
        webDb = null; // Ensure webDb is also null if Auth fails
        return false;
      }
      
      if (!webDb) {
        console.error('❌ Failed to get Firestore instance');
        return false;
      }
      
      // Note: Not calling enableNetwork() as it can cause internal assertion errors
      // Firestore is online by default, and calling enableNetwork on an already-online
      // instance can trigger "INTERNAL ASSERTION FAILED" errors
      // If you need offline persistence, configure it during initialization instead
      
      console.log('✅ Firebase initialized for web - webAuth and webDb are ready');
      console.log('✅ Connected to Firestore database: (default) in project:', firebaseConfig.projectId);
      console.log('✅ Web and mobile now use the same database for unified data');
      console.log('🔍 Firebase Auth Project:', firebaseConfig.projectId);
      console.log('🔍 Firebase Auth Domain:', firebaseConfig.authDomain);
      return true;
    } catch (error: any) {
      console.error('❌ Error initializing Firebase for web:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      webAuth = null;
      webDb = null;
      webStorage = null;
      return false;
    }
  } else {
    // Mobile Firebase (React Native) - modular API (v22)
    try {
      if (typeof require !== 'undefined') {
        const { getApp } = require('@react-native-firebase/app');
        const { getAuth } = require('@react-native-firebase/auth');
        const { getFirestore } = require('@react-native-firebase/firestore');
        const app = getApp();
        mobileAuth = getAuth(app);
        mobileFirestore = getFirestore(app);
        console.log('✅ Firebase initialized for mobile');
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('❌ Error initializing Firebase for mobile:', error);
      console.error('Error details:', error.message);
      console.log('💡 Make sure @react-native-firebase packages are installed');
      console.log('💡 Make sure Firebase is properly configured in native code (AppDelegate for iOS, MainApplication for Android)');
      mobileAuth = null;
      mobileFirestore = null;
      return false;
    }
  }
};

// Initialize on first import (only for web, mobile will initialize when needed)
if (Platform.OS === 'web') {
  initializeFirebase();
}

// Export webDb getter for use in other files
export const getWebDb = (): any => {
  // Ensure Firebase is initialized if not already
  if (Platform.OS === 'web') {
    if (!webDb) {
      const initialized = initializeFirebase();
      if (!initialized) {
        console.warn('⚠️  getWebDb: Firebase initialization failed, returning null');
        return null;
      }
    }
    // Verify webDb is valid before returning
    if (!webDb) {
      console.warn('⚠️  getWebDb: webDb is null after initialization attempt');
      return null;
    }
    // Basic validation: check if webDb is an object and has Firestore-like properties
    if (typeof webDb !== 'object' || webDb === null) {
      console.warn('⚠️  getWebDb: webDb is not a valid object');
      // Try to re-initialize
      const reinitialized = initializeFirebase();
      if (!reinitialized || !webDb || typeof webDb !== 'object') {
        console.warn('⚠️  getWebDb: Re-initialization failed, returning null');
        return null;
      }
    }
    
    // Additional validation: check if it looks like a Firestore instance
    // Firestore instances have _delegate or _settings properties
    if (!webDb._delegate && !webDb._settings && !webDb.type) {
      console.warn('⚠️  getWebDb: webDb does not appear to be a valid Firestore instance');
      // Try to re-initialize one more time
      const reinitialized = initializeFirebase();
      if (!reinitialized || !webDb) {
        return null;
      }
    }
  }
  return webDb;
};

// Export webAuth getter for use in other files
export const getWebAuth = (): any => {
  // Ensure Firebase is initialized if not already
  if (!webAuth && Platform.OS === 'web') {
    initializeFirebase();
  }
  return webAuth;
};

// Export mobileFirestore getter for use in other files
// Returns a function that returns the Firestore instance (modular API: mobileFirestore is the db instance)
export const getMobileFirestore = (): (() => any) | null => {
  if (Platform.OS !== 'web') {
    if (!mobileFirestore) {
      const initialized = initializeFirebase();
      if (!initialized || !mobileFirestore) {
        console.warn('⚠️  getMobileFirestore: Firebase initialization failed, returning null');
        return null;
      }
    }
    return () => mobileFirestore;
  }
  return null;
};

/** Native Firebase Auth instance (modular v22 API). Mirrors getMobileFirestore. */
export const getMobileAuth = (): any => {
  if (Platform.OS !== 'web') {
    if (!mobileAuth) {
      const initialized = initializeFirebase();
      if (!initialized || !mobileAuth) {
        console.warn('⚠️  getMobileAuth: Firebase initialization failed, returning null');
        return null;
      }
    }
    return mobileAuth;
  }
  return null;
};

// Export initializeFirebase for use in other files
export { initializeFirebase };

// Export webDb and webAuth for direct access (same module instance)
export const getWebDbInstance = () => webDb;
export const getWebAuthInstance = () => webAuth;

/** Firebase Storage (web only). Used for product image uploads. */
export const getWebStorage = (): any => {
  if (Platform.OS !== 'web') return null;
  if (!webStorage) {
    const initialized = initializeFirebase();
    if (!initialized || !webStorage) return null;
  }
  return webStorage;
};

// Export Firestore functions so all files use the same module instance
export const getFirestoreFunctions = () => {
  if (Platform.OS === 'web') {
    try {
      const firestore = require('firebase/firestore');
      return firestore;
    } catch (error) {
      console.error('❌ Error requiring firebase/firestore:', error);
      return null;
    }
  }
  return null;
};

/**
 * Firestore database from the same firebase/firestore module as getFirestoreFunctions().
 * Using getWebDb() with doc() from a different bundle causes:
 * "Expected first argument to collection() to be a CollectionReference".
 */
export const getWebFirestoreDb = (): any => {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const firestoreMod = getFirestoreFunctions();
    if (!firestoreMod) {
      return null;
    }
    initializeFirebase();
    const { getFirestore } = firestoreMod;
    const { getApps } = require('firebase/app');
    const apps = getApps();
    if (!apps.length) {
      return null;
    }
    const db = getFirestore(apps[0]);
    const isValid =
      !!db &&
      typeof db === 'object' &&
      ('_delegate' in db || '_settings' in db || 'type' in db);
    return isValid ? db : null;
  } catch (error) {
    console.error('❌ getWebFirestoreDb:', error);
    return null;
  }
};

/** doc/setDoc/getDoc helpers bound to the same Firestore instance (web only). */
export const getWebFirestoreClient = (): {
  firestoreMod: typeof import('firebase/firestore');
  db: any;
} | null => {
  const firestoreMod = getFirestoreFunctions();
  const db = getWebFirestoreDb();
  if (!firestoreMod || !db) {
    return null;
  }
  return { firestoreMod, db };
};

// Care roles: under customer (userType === 'customer'), optional sub-role for Elderly Care Coordination
export type CareRole = 'family' | 'senior' | 'professional';

export interface User {
  id: string;
  email: string;
  name: string;
  phone: string;
  userType: 'customer' | 'business_owner';
  createdAt: string;
  lastLogin?: string;
  username?: string; // Use email as username
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
  };
  /** When userType === 'customer', optional care coordination role (Family / Senior / Care professional) */
  careRole?: CareRole;
  /** When careRole is 'family' or 'professional': userId of the senior/care recipient they are linked to. */
  linkedCareRecipientId?: string;
}

/** ProductionStorage key for cached user profile (persists as `bookapt_user` in localStorage). */
const USER_CACHE_STORAGE_KEY = 'user';

const FIRESTORE_GET_USER_TIMEOUT_MS = 4000;

function getUserDocUid(user: User & { uid?: string }): string {
  return user.id || user.uid || '';
}

function cachedUserMatchesAuth(cached: User, authUid: string): boolean {
  const cachedUid = getUserDocUid(cached);
  return !!cachedUid && cachedUid === authUid;
}

async function loadCachedUserForUid(authUid: string): Promise<User | null> {
  const ProductionStorage = require('../utils/productionStorage').default;
  for (const key of [USER_CACHE_STORAGE_KEY, 'bookapt_user']) {
    try {
      const localUser = await ProductionStorage.getItem(key);
      if (!localUser) continue;
      const parsed = JSON.parse(localUser) as User;
      if (cachedUserMatchesAuth(parsed, authUid)) return parsed;
    } catch {
      /* ignore malformed cache */
    }
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const legacy = localStorage.getItem('bookapt_bookapt_user');
      if (legacy) {
        const parsed = JSON.parse(legacy) as User;
        if (cachedUserMatchesAuth(parsed, authUid)) {
          await ProductionStorage.setItem(USER_CACHE_STORAGE_KEY, legacy);
          return parsed;
        }
      }
    } catch {
      /* ignore */
    }
  }
  return null;
}

async function saveCachedUser(user: User): Promise<void> {
  const ProductionStorage = require('../utils/productionStorage').default;
  await ProductionStorage.setItem(USER_CACHE_STORAGE_KEY, JSON.stringify(user));
  try {
    await ProductionStorage.removeItem('bookapt_user');
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      localStorage.removeItem('bookapt_bookapt_user');
    } catch {
      /* ignore */
    }
  }
}

async function clearAllAuthSessionCache(): Promise<void> {
  const ProductionStorage = require('../utils/productionStorage').default;
  await ProductionStorage.clearAuthSessionCaches();
  try {
    const { AuthManager } = require('../utils/authManager');
    AuthManager.clearUserCache();
    AuthManager.clearBusinessOwnerCache();
  } catch {
    /* ignore */
  }
}

export function businessOwnerMatchesAuth(
  owner: { id?: string; uid?: string; ownerId?: string; email?: string; username?: string },
  firebaseUser: { uid: string; email?: string | null }
): boolean {
  const ownerId = owner.id || owner.uid || owner.ownerId;
  if (ownerId && ownerId !== firebaseUser.uid) {
    return false;
  }
  const authEmail = normalizeAuthEmail(firebaseUser.email);
  const ownerEmail = normalizeAuthEmail(owner.email || owner.username);
  if (authEmail && ownerEmail && authEmail !== ownerEmail) {
    return false;
  }
  return true;
}

/** Prefer email for UI labels — names in Firestore/cache are often stale after account switches. */
export function getAuthSessionDisplayLabel(user: User | null | undefined): string {
  if (!user) return 'User';
  return user.email || user.username || user.name || 'User';
}

function buildUserFromFirebaseAuth(firebaseUser: {
  uid: string;
  email?: string | null;
  displayName?: string | null;
}): User {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    phone: '',
    userType: 'customer',
    createdAt: new Date().toISOString(),
    username: firebaseUser.email || '',
  };
}

function normalizeAuthEmail(email?: string | null): string {
  return (email || '').trim().toLowerCase();
}

/**
 * Firebase Auth email is canonical for the active session.
 * Firestore/cache may be stale — never show another account's email in the UI.
 */
export function enforceAuthIdentityOnProfile(
  profile: User,
  firebaseUser: { uid: string; email?: string | null; displayName?: string | null }
): User {
  const authEmail = (firebaseUser.email || '').trim();
  const profileEmail = (profile.email || profile.username || '').trim();
  const aligned: User = {
    ...profile,
    id: firebaseUser.uid,
  };

  if (!authEmail) {
    return aligned;
  }

  if (normalizeAuthEmail(profileEmail) !== normalizeAuthEmail(authEmail)) {
    console.warn(
      '⚠️ UnifiedAuthService: Profile email does not match Firebase Auth — correcting',
      { profileEmail, authEmail, uid: firebaseUser.uid, profileName: profile.name }
    );
    aligned.email = authEmail;
    aligned.username = authEmail;
    // Stale Firestore/cache often keeps the previous account holder's display name
    aligned.name =
      firebaseUser.displayName?.trim() ||
      authEmail.split('@')[0] ||
      aligned.name;
  }

  return aligned;
}

/** If users/{uid} says customer but businesses/{uid} exists, treat as business owner. */
async function applyBusinessOwnerTypeFromFirestore(user: User, authUid: string): Promise<User> {
  if (!webDb || user.userType === 'business_owner') {
    return user;
  }
  try {
    const { doc, getDoc } = require('firebase/firestore');
    const bizDoc: any = await Promise.race([
      getDoc(doc(webDb, 'businesses', authUid)),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 2000)),
    ]);
    if (bizDoc && typeof bizDoc.exists === 'function' && bizDoc.exists()) {
      const bizName = (bizDoc.data()?.name as string | undefined)?.trim();
      if (bizName) {
        console.log('✅ UnifiedAuthService: Detected business account from businesses/', authUid);
        return {
          ...user,
          userType: 'business_owner',
          name: bizName,
        };
      }
    }
  } catch {
    /* ignore */
  }
  return user;
}

async function finalizeWebUserProfile(
  profile: User,
  firebaseUser: { uid: string; email?: string | null; displayName?: string | null }
): Promise<User> {
  const aligned = enforceAuthIdentityOnProfile(profile, firebaseUser);
  const emailCorrected =
    normalizeAuthEmail(aligned.email) !== normalizeAuthEmail(profile.email) ||
    normalizeAuthEmail(aligned.username || '') !== normalizeAuthEmail(profile.username || profile.email);

  if (emailCorrected) {
    try {
      await saveCachedUser(aligned);
    } catch {
      /* ignore */
    }
    if (webDb) {
      try {
        const { doc, setDoc } = require('firebase/firestore');
        setDoc(
          doc(webDb, 'users', firebaseUser.uid),
          {
            email: aligned.email,
            username: aligned.username,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        ).catch(() => {
          /* non-blocking */
        });
      } catch {
        /* ignore */
      }
    }
  }

  return await applyBusinessOwnerTypeFromFirestore(aligned, firebaseUser.uid);
}

/**
 * Recursively strips undefined values from an object.
 * Firestore rejects "undefined" - use this before any Firestore write.
 */
export function sanitizeForFirestore<T>(obj: T): T {
  if (obj === undefined || obj === null) return obj;
  if (typeof obj !== 'object') return obj;
  if (obj instanceof Date) return obj;
  if (Array.isArray(obj)) return obj.map(sanitizeForFirestore) as T;
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (v !== undefined) {
      result[k] = typeof v === 'object' && v !== null && !(v instanceof Date)
        ? sanitizeForFirestore(v)
        : v;
    }
  }
  return result as T;
}

export class UnifiedAuthService {
  // Register new user
  static async registerUser(
    email: string,
    password: string,
    userData: {
      name: string;
      phone: string;
      userType: 'customer' | 'business_owner';
      address?: {
        street?: string;
        city?: string;
        state?: string;
        zipcode?: string;
        country?: string;
      };
      /** For customer accounts: 'family' | 'senior' | 'professional' (Elderly Care Coordination) */
      careRole?: CareRole;
    }
  ): Promise<User> {
    try {
      console.log('🔐 UnifiedAuthService: Starting registration for:', email);
      console.log('🔐 Platform:', Platform.OS);
      
      if (Platform.OS === 'web') {
        // Web implementation
        if (!webAuth || !webDb) {
          const initialized = initializeFirebase();
          if (!initialized || !webAuth) {
            const errorMsg = 'Firebase authentication is not properly configured. Please refresh the page and try again.';
            console.error('❌', errorMsg);
            console.error('webAuth is null:', webAuth === null);
            console.error('webDb is null:', webDb === null);
            throw new Error(errorMsg);
          }
        }
        
        // Double-check webAuth is initialized
        if (!webAuth) {
          const errorMsg = 'Firebase authentication is not properly configured. Please refresh the page and try again.';
          console.error('❌', errorMsg);
          throw new Error(errorMsg);
        }
        
        const { createUserWithEmailAndPassword } = require('firebase/auth');
        const { doc, setDoc, collection } = require('firebase/firestore');
        
        console.log('🔐 Creating Firebase Auth account...');
        console.log('🔍 Firebase Auth Project Info:', {
          projectId: firebaseConfig.projectId,
          authDomain: firebaseConfig.authDomain,
          apiKey: firebaseConfig.apiKey ? firebaseConfig.apiKey.substring(0, 20) + '...' : 'missing',
        });
        console.log('🔍 webAuth object:', webAuth ? 'exists' : 'null');
        console.log('🔍 Email:', email);
        console.log('🔍 Password length:', password ? password.length : 0);
        
        let userCredential;
        try {
          userCredential = await createUserWithEmailAndPassword(
            webAuth,
            email,
            password
          );
        } catch (authError: any) {
          console.error('❌ Firebase Auth createUserWithEmailAndPassword error:', authError);
          console.error('❌ Error code:', authError?.code);
          console.error('❌ Error message:', authError?.message);
          console.error('❌ Full error object:', JSON.stringify(authError, null, 2));
          throw authError;
        }
        
        const firebaseUser = userCredential.user;
        console.log('✅ Firebase Auth account created:', firebaseUser.uid);
        
        // Wait a moment for auth state to propagate (Firestore rules check request.auth)
        console.log('🔐 Waiting for auth state to propagate...');
        await new Promise<void>(resolve => setTimeout(() => resolve(), 100));
        console.log('🔐 Auth state should be ready now');
        
        // Verify auth state is set
        const currentAuthUser = webAuth.currentUser;
        if (!currentAuthUser || currentAuthUser.uid !== firebaseUser.uid) {
          console.warn('⚠️ Auth state not yet propagated, waiting a bit more...');
          await new Promise<void>(resolve => setTimeout(() => resolve(), 500));
        }
        console.log('🔐 Current auth user:', webAuth.currentUser?.uid);
        console.log('🔐 Expected user UID:', firebaseUser.uid);
        console.log('🔐 Auth state matches?', webAuth.currentUser?.uid === firebaseUser.uid);
        
        // Create user document in Firestore
        const userDoc: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          name: userData.name,
          phone: userData.phone,
          userType: userData.userType,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          username: firebaseUser.email!, // Use email as username
          address: userData.address,
          ...(userData.careRole != null && { careRole: userData.careRole }),
        };
        // Firestore rejects undefined - sanitize optional fields (address, etc.)
        const sanitizedUserDoc = sanitizeForFirestore(userDoc) as User;
        
        console.log('🔐 Saving user data to Firestore...');
        console.log('🔐 Firestore DB initialized:', webDb ? 'YES' : 'NO');
        console.log('🔐 User authenticated:', firebaseUser ? 'YES' : 'NO');
        console.log('🔐 User UID:', firebaseUser.uid);
        console.log('🔐 User document to save:', JSON.stringify(userDoc, null, 2));
        console.log('🔐 User name:', userDoc.name);
        console.log('🔐 User address:', JSON.stringify(userDoc.address, null, 2));
        console.log('🔐 Collection path: users');
        console.log('🔐 Document ID:', firebaseUser.uid);
        
        // Verify Firestore is initialized
        if (!webDb) {
          const errorMsg = 'Firestore database not initialized. Cannot save user data.';
          console.error('❌', errorMsg);
          throw new Error(errorMsg);
        }
        
        // Verify user is authenticated
        if (!firebaseUser || !firebaseUser.uid) {
          const errorMsg = 'User not authenticated. Cannot save user data.';
          console.error('❌', errorMsg);
          throw new Error(errorMsg);
        }
        
        try {
          // First, test Firestore connectivity with a simple write
          console.log('🔐 Testing Firestore connectivity...');
          try {
            const { writeBatch } = require('firebase/firestore');
            const testBatch = writeBatch(webDb);
            const testDocRef = doc(webDb, '_test', 'connectivity');
            testBatch.set(testDocRef, { test: true, timestamp: new Date().toISOString() });
            
            const testTimeout = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Firestore connectivity test timeout')), 3000)
            );
            
            await Promise.race([testBatch.commit(), testTimeout]);
            console.log('✅ Firestore connectivity test passed');
            // Clean up test document
            try {
              const { deleteDoc } = require('firebase/firestore');
              await deleteDoc(testDocRef);
            } catch (cleanupError) {
              // Ignore cleanup errors
            }
          } catch (connectivityError: any) {
            console.error('❌❌❌ FIRESTORE CONNECTIVITY TEST FAILED ❌❌❌');
            console.error('❌ Firestore is not reachable. This means:');
            console.error('❌ 1. Browser extension (ad blocker) is blocking Firestore');
            console.error('❌ 2. Network firewall is blocking Firestore');
            console.error('❌ 3. Firestore service is down');
            console.error('❌ 4. Check Network tab (F12) for blocked requests to firestore.googleapis.com');
            console.error('❌ Connectivity error:', connectivityError.message);
            throw new Error('Firestore connectivity test failed - Check Network tab for blocked requests to firestore.googleapis.com');
          }
          
          const userDocRef = doc(webDb, 'users', firebaseUser.uid);
          console.log('🔐 Attempting to save to Firestore path: users/' + firebaseUser.uid);
          console.log('🔐 About to call setDoc...');
          
          // Add timeout to setDoc to prevent hanging
          console.log('🔐 Calling setDoc now...');
          console.log('🔐 User UID for rule check:', firebaseUser.uid);
          console.log('🔐 Document path:', 'users/' + firebaseUser.uid);
          console.log('🔐 Auth user UID:', webAuth.currentUser?.uid);
          console.log('🔐 UIDs match?', firebaseUser.uid === webAuth.currentUser?.uid);
          
          // Try using writeBatch instead of setDoc for better error handling
          try {
            const { writeBatch } = require('firebase/firestore');
            const batch = writeBatch(webDb);
            batch.set(userDocRef, sanitizedUserDoc);
            
            const batchPromise = batch.commit();
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => {
                console.error('❌❌❌ FIRESTORE WRITE TIMEOUT - Request is hanging!');
                console.error('❌ Check Network tab (F12) for blocked requests to firestore.googleapis.com');
                console.error('❌ Look for requests with status: blocked, failed, or pending');
                reject(new Error('Firestore write timeout after 5 seconds - Check Network tab for blocked requests'));
              }, 5000)
            );
            
            await Promise.race([batchPromise, timeoutPromise]);
            console.log('✅ User data saved to Firestore successfully (using batch write)');
            console.log('✅ Document path: users/' + firebaseUser.uid);
          } catch (batchError: any) {
            // If batch write fails, try setDoc as fallback
            console.warn('⚠️  Batch write failed, trying setDoc as fallback:', batchError.message);
            
            const setDocPromise = setDoc(userDocRef, sanitizedUserDoc);
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => {
                console.error('❌❌❌ SETDOC TIMEOUT - Firestore write is hanging!');
                console.error('❌ This usually means:');
                console.error('❌ 1. Firestore security rules are blocking the write');
                console.error('❌ 2. Network connectivity issue');
                console.error('❌ 3. Firestore is blocked by browser/ad blocker');
                console.error('❌ 4. Firestore database not properly initialized');
                console.error('❌ Check Network tab (F12) for blocked requests to firestore.googleapis.com');
                reject(new Error('setDoc timeout after 5 seconds - Check Network tab for blocked requests'));
              }, 5000)
            );
            
            try {
              await Promise.race([setDocPromise, timeoutPromise]);
              console.log('✅ User data saved to Firestore successfully (using setDoc fallback)');
              console.log('✅ Document path: users/' + firebaseUser.uid);
            } catch (setDocError: any) {
              console.error('❌ setDoc failed or timed out:', setDocError);
              console.error('❌ Error details:', {
                message: setDocError.message,
                code: setDocError.code,
                stack: setDocError.stack
              });
              throw setDocError; // Re-throw to be caught by outer catch
            }
          }
          
          // Verify the document was actually saved by reading it back
          try {
            const { getDoc } = require('firebase/firestore');
            const verifyDoc = await getDoc(userDocRef);
            if (verifyDoc.exists()) {
              console.log('✅ Verified: User document exists in Firestore');
              console.log('✅ Verified data:', JSON.stringify(verifyDoc.data(), null, 2));
            } else {
              console.warn('⚠️  WARNING: Document save reported success but document does not exist!');
            }
          } catch (verifyError: any) {
            console.warn('⚠️  Could not verify document save:', verifyError.message);
          }
          
          // Also save to local storage immediately for fast access
          try {
            await saveCachedUser(sanitizedUserDoc);
            console.log('✅ User data saved to local storage during registration');
          } catch (localStorageError: any) {
            console.warn('⚠️ Could not save user to local storage during registration:', localStorageError);
            // Don't throw - Firestore save succeeded
          }
          
          // Initialize 5 free credits for new customers
          if (userData.userType === 'customer') {
            try {
              const userDocRef = doc(webDb, 'users', firebaseUser.uid);
              const creditsRef = collection(userDocRef, 'credits');
              const creditsDocRef = doc(creditsRef, 'current');
              const initialCredits = {
                userId: firebaseUser.uid,
                availableCredits: 5,
                totalCredits: 5,
                usedCredits: 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              await setDoc(creditsDocRef, initialCredits);
              console.log('✅ Initialized 5 free credits for new customer');
            } catch (creditsError: any) {
              console.error('❌ Error initializing credits:', creditsError);
              // Don't throw - user registration succeeded
            }
          }
          
          // If user is a business owner, also create business document
          if (userData.userType === 'business_owner') {
            try {
              // Sales rep referral attribution (bookapt.io/join?repCode=...) -
              // must be captured HERE, at account creation, or it's gone for
              // good afterward. See widgetEmbed.ts's getReferralRepCodeFromUrl.
              const { getReferralRepCodeFromUrl } = require('../utils/widgetEmbed');
              const referredBySalesRepCode = getReferralRepCodeFromUrl();

              const businessDocData = sanitizeForFirestore({
                id: firebaseUser.uid,
                ownerId: firebaseUser.uid,
                name: userData.name,
                description: '',
                category: 'General',
                address: userData.address || {},
                phone: userData.phone || '',
                email: firebaseUser.email!,
                website: '',
                isActive: true,
                isOnBookAPT: true, // Business registered on BookAPT platform
                // Default: allow Confirm booking unless business turns on prepay in Settings
                paymentRequiredAtBooking: false,
                ...(referredBySalesRepCode ? { referredBySalesRepCode } : {}),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              await setDoc(doc(webDb, 'businesses', firebaseUser.uid), businessDocData);
              console.log('✅ Business document created in Firestore');
              console.log('✅ Document path: businesses/' + firebaseUser.uid);
            } catch (businessError: any) {
              console.error('❌ Error creating business document:', businessError);
              // Don't throw - user document was created successfully
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ Firestore save error:', firestoreError);
          console.error('❌ Error code:', firestoreError.code);
          console.error('❌ Error message:', firestoreError.message);
          console.error('❌ Error stack:', firestoreError.stack);
          console.error('❌ Full error:', JSON.stringify(firestoreError, Object.getOwnPropertyNames(firestoreError), 2));
          
          // Save to local storage as fallback (always do this)
          try {
            await saveCachedUser(sanitizedUserDoc);
            console.log('✅ User data saved to local storage as fallback');
          } catch (localStorageError: any) {
            console.error('❌ Could not save to local storage either:', localStorageError);
          }
          
          // Check for common Firestore errors
          if (firestoreError.code === 'permission-denied') {
            const errorMsg = '❌❌❌ FIRESTORE PERMISSION DENIED ❌❌❌';
            console.error('❌', errorMsg);
            console.error('❌ The user was created in Authentication, but Firestore save FAILED!');
            console.error('❌ This is why you see user in Auth but NOT in Firestore Database!');
            console.error('❌');
            console.error('❌ FIX: Go to Firebase Console → Firestore Database → Rules tab');
            console.error('❌ Add this rule:');
            console.error('❌ match /users/{userId} {');
            console.error('❌   allow read: if request.auth != null;');
            console.error('❌   allow write: if request.auth != null && request.auth.uid == userId;');
            console.error('❌ }');
            console.error('❌ Then click "Publish"');
            console.error('❌');
            // Don't throw - Auth succeeded, but warn user
            console.warn('⚠️  User registered in Firebase Auth but Firestore save failed due to permissions');
            console.warn('⚠️  User can still log in, but data is only in local storage (not synced)');
            
            // Create a special error type for user-friendly messaging
            const syncError = new Error('ACCOUNT_CREATED_BUT_SYNC_FAILED');
            (syncError as any).code = 'sync-failed';
            (syncError as any).userMessage = 'Your account was created successfully! We\'re having trouble syncing your profile data. Don\'t worry - your account is saved and we\'ll sync your data automatically when your connection improves.';
            (syncError as any).canRetry = true;
            throw syncError;
          } else if (firestoreError.code === 'unavailable' || firestoreError.message?.includes('offline') || firestoreError.message?.includes('timeout')) {
            console.warn('⚠️  Firestore unavailable or timeout. Saving to local storage only.');
            
            // Queue for background sync
            try {
              const { backgroundSyncService } = await import('./backgroundSyncService');
              await backgroundSyncService.queueWrite('users', firebaseUser.uid, sanitizedUserDoc);
              console.log('✅ Queued user data for background sync (timeout)');
            } catch (queueError: any) {
              console.error('❌ Could not queue for background sync:', queueError);
            }
            
            // Create user-friendly error for timeout/unavailable
            const syncError = new Error('ACCOUNT_CREATED_BUT_SYNC_TIMEOUT');
            (syncError as any).code = 'sync-timeout';
            (syncError as any).userMessage = 'Your account was created successfully! We\'re syncing your profile data in the background. This may take a moment.';
            (syncError as any).canRetry = true;
            throw syncError;
          } else {
            // Other errors - still try to save to local storage
            console.warn('⚠️  User registered in Firebase Auth but Firestore save failed');
            console.warn('⚠️  Check Firestore security rules and database setup');
            
            // Queue for background sync
            try {
              const { backgroundSyncService } = await import('./backgroundSyncService');
              await backgroundSyncService.queueWrite('users', firebaseUser.uid, sanitizedUserDoc);
              console.log('✅ Queued user data for background sync');
            } catch (queueError: any) {
              console.error('❌ Could not queue for background sync:', queueError);
            }
            
            // Create user-friendly error
            const syncError = new Error('ACCOUNT_CREATED_BUT_SYNC_FAILED');
            (syncError as any).code = 'sync-failed';
            (syncError as any).userMessage = 'Your account was created successfully! We\'re having trouble syncing your profile data. Don\'t worry - your account is saved and we\'ll sync your data automatically when your connection improves.';
            (syncError as any).canRetry = true;
            throw syncError;
          }
        }
        
        return sanitizedUserDoc;
      } else {
        // Mobile implementation
        if (!mobileAuth || !mobileFirestore) {
          initializeFirebase();
        }
        
        // Dynamic require for mobile (prevents webpack from bundling)
        if (!mobileAuth || !mobileFirestore) {
          throw new Error('React Native Firebase packages not installed. Install @react-native-firebase/auth and @react-native-firebase/firestore');
        }
        
        console.log('🔐 Creating Firebase Auth account (mobile)...');
        const { createUserWithEmailAndPassword } = require('@react-native-firebase/auth');
        const userCredential = await createUserWithEmailAndPassword(mobileAuth, email, password);
        
        const firebaseUser = userCredential.user;
        console.log('✅ Firebase Auth account created:', firebaseUser.uid);
        
        // Wait a moment for auth state to propagate (Firestore rules check request.auth)
        console.log('🔐 Waiting for auth state to propagate (mobile)...');
        await new Promise<void>(resolve => setTimeout(() => resolve(), 100));
        console.log('🔐 Auth state should be ready now (mobile)');
        
        // Verify auth state is set
        const currentAuthUser = mobileAuth.currentUser;
        if (!currentAuthUser || currentAuthUser.uid !== firebaseUser.uid) {
          console.warn('⚠️ Auth state not yet propagated (mobile), waiting a bit more...');
          await new Promise<void>(resolve => setTimeout(() => resolve(), 500));
        }
        console.log('🔐 Current auth user (mobile):', mobileAuth.currentUser?.uid);
        console.log('🔐 Expected user UID (mobile):', firebaseUser.uid);
        console.log('🔐 Auth state matches? (mobile):', mobileAuth.currentUser?.uid === firebaseUser.uid);
        
        // Create user document in Firestore
        const userDoc: User = {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          name: userData.name,
          phone: userData.phone,
          userType: userData.userType,
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          username: firebaseUser.email!, // Use email as username
          address: userData.address,
          ...(userData.careRole != null && { careRole: userData.careRole }),
        };
        // Firestore rejects undefined - sanitize optional fields (address, etc.)
        const sanitizedUserDoc = sanitizeForFirestore(userDoc) as User;
        
        console.log('🔐 Saving user data to Firestore (mobile)...');
        console.log('🔐 Firestore DB initialized:', mobileFirestore ? 'YES' : 'NO');
        console.log('🔐 User authenticated:', firebaseUser ? 'YES' : 'NO');
        console.log('🔐 User UID:', firebaseUser.uid);
        console.log('🔐 Collection path: users');
        console.log('🔐 Document ID:', firebaseUser.uid);
        
        try {
          const { doc, setDoc, getDoc, collection } = require('@react-native-firebase/firestore');
          await setDoc(doc(mobileFirestore, 'users', firebaseUser.uid), sanitizedUserDoc);
          console.log('✅ User data saved to Firestore (mobile)');
          
          // Verify document was saved
          try {
            const verifyDoc = await getDoc(doc(mobileFirestore, 'users', firebaseUser.uid));
            if (verifyDoc.exists()) {
              console.log('✅ Verified: User document exists in Firestore (mobile)');
            } else {
              console.warn('⚠️  WARNING: Document save reported success but document does not exist! (mobile)');
            }
          } catch (verifyError: any) {
            console.warn('⚠️  Could not verify document save (mobile):', verifyError.message);
          }
          
          // Initialize 5 free credits for new customers
          if (userData.userType === 'customer') {
            try {
              const initialCredits = {
                userId: firebaseUser.uid,
                availableCredits: 5,
                totalCredits: 5,
                usedCredits: 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              };
              await setDoc(doc(collection(mobileFirestore, 'users', firebaseUser.uid, 'credits'), 'current'), initialCredits);
              console.log('✅ Initialized 5 free credits for new customer (mobile)');
            } catch (creditsError: any) {
              console.error('❌ Error initializing credits (mobile):', creditsError);
              // Don't throw - user registration succeeded
            }
          }
          
          // If user is a business owner, also create business document
          if (userData.userType === 'business_owner') {
            try {
              const businessDocData = sanitizeForFirestore({
                id: firebaseUser.uid,
                ownerId: firebaseUser.uid,
                name: userData.name,
                description: '',
                category: 'General',
                address: userData.address || {},
                phone: userData.phone || '',
                email: firebaseUser.email!,
                website: '',
                isActive: true,
                isOnBookAPT: true, // Business registered on BookAPT platform
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
              });
              await setDoc(doc(mobileFirestore, 'businesses', firebaseUser.uid), businessDocData);
              console.log('✅ Business document created in Firestore (mobile)');
              console.log('✅ Document path: businesses/' + firebaseUser.uid);
            } catch (businessError: any) {
              console.error('❌ Error creating business document (mobile):', businessError);
              // Don't throw - user document was created successfully
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ Firestore save error (mobile):', firestoreError);
          console.error('❌ Error code:', firestoreError.code);
          console.error('❌ Error message:', firestoreError.message);
          
          // Check for common Firestore errors
          if (firestoreError.code === 'permission-denied') {
            const errorMsg = '❌❌❌ FIRESTORE PERMISSION DENIED (MOBILE) ❌❌❌';
            console.error('❌', errorMsg);
            console.error('❌ The user was created in Authentication, but Firestore save FAILED!');
            console.error('❌ Both web and mobile use DEFAULT database - ensure rules are set correctly');
            console.error('❌');
            console.error('❌ FIX: Go to Firebase Console → Firestore Database → Select "(default)" database → Rules tab');
            console.error('❌ Ensure security rules allow authenticated users to create their own documents');
            console.error('❌ Then click "Publish"');
            console.error('❌');
            console.error('❌ See FIX_MOBILE_FIRESTORE_DATABASE.md for detailed instructions');
            console.error('❌');
            
            // Create a special error type for user-friendly messaging
            const syncError = new Error('ACCOUNT_CREATED_BUT_SYNC_FAILED');
            (syncError as any).code = 'sync-failed';
            (syncError as any).userMessage = 'Your account was created successfully! We\'re having trouble syncing your profile data. Don\'t worry - your account is saved and we\'ll sync your data automatically when your connection improves.';
            (syncError as any).canRetry = true;
            throw syncError;
          }
          
          // Re-throw other errors
          throw firestoreError;
        }
        
        return sanitizedUserDoc;
      }
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      console.error('❌ Error type:', typeof error);
      console.error('❌ Error code:', error?.code);
      console.error('❌ Error message:', error?.message);
      console.error('❌ Error stack:', error?.stack);
      console.error('❌ Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
      
      // Handle specific Firebase errors
      let errorMessage = 'Registration failed. Please try again.';
      
      // Check for sync-failed errors (account created but Firestore save failed)
      if (error.code === 'sync-failed' || error.code === 'sync-timeout') {
        // Re-throw as-is - it has userMessage already
        throw error;
      }
      
      // Check for null/undefined errors (Firebase not initialized)
      if (error.message?.includes('Cannot read properties of null') || 
          error.message?.includes('reading \'app\'') ||
          error.message?.includes('Firebase authentication is not properly configured')) {
        errorMessage = 'Firebase is not properly configured. Please refresh the page and try again.';
      } else if (error.code === 'auth/email-already-in-use') {
        // User exists in Firebase Auth but may not have Firestore document (migration scenario)
        errorMessage = 'This email is already registered in Firebase Auth. This may be from a previous database. Please sign in with your password, or if you forgot it, use password reset. If you need to re-register, the account must be deleted from Firebase Auth first.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'permission-denied' || error.message?.includes('permission-denied')) {
        errorMessage = 'Permission denied. Please check Firestore security rules. Mobile app uses default database - ensure rules are set for (default) database.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }
  
  // Login user
  static async loginUser(
    email: string,
    password: string
  ): Promise<User> {
    try {
      console.log('🔐 UnifiedAuthService: Starting login for:', email);
      console.log('🔐 Platform:', Platform.OS);
      
      if (Platform.OS === 'web') {
        // Web implementation
        if (!webAuth || !webDb) {
          initializeFirebase();
        }
        
        const { signInWithEmailAndPassword } = require('firebase/auth');
        const { doc, getDoc, setDoc } = require('firebase/firestore');
        
        console.log('🔐 Signing in with Firebase Auth...');
        const userCredential = await signInWithEmailAndPassword(
          webAuth,
          email,
          password
        );
        
        const firebaseUser = userCredential.user;
        console.log('✅ Firebase Auth sign-in successful:', firebaseUser.uid);

        // Drop any profile cached for a different account before loading the new user
        await clearAllAuthSessionCache();
        
        // Get user data from Firestore (with timeout to prevent blocking)
        console.log('🔐 Getting user data from Firestore...');
        let userData: User;
        
        try {
          // Add timeout to prevent blocking
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Firestore timeout')), FIRESTORE_GET_USER_TIMEOUT_MS)
          );
          
          let userDoc: any = null;
          try {
            const userDocPromise = getDoc(doc(webDb, 'users', firebaseUser.uid));
            userDoc = await Promise.race([userDocPromise, timeoutPromise]);
          } catch (timeoutError: any) {
            // Timeout or other error - fall through to fallback logic
            if (timeoutError.message === 'Firestore timeout') {
              console.warn('⚠️ Firestore timeout (2s), falling back to local storage');
            } else {
              throw timeoutError; // Re-throw if it's not a timeout
            }
          }
          
          if (userDoc && userDoc.exists()) {
            // Successfully loaded from Firestore
            userData = enforceAuthIdentityOnProfile(userDoc.data() as User, firebaseUser);
            console.log('✅ User data retrieved from Firestore:', userData.email);
            
            // Update last login (async, don't wait)
            setDoc(
              doc(webDb, 'users', firebaseUser.uid),
              { lastLogin: new Date().toISOString() },
              { merge: true }
            ).catch((err: any) => {
              console.warn('⚠️ Could not update last login in Firestore:', err);
            });
          } else {
            // Firestore timeout or document doesn't exist — use Auth email only (never another user's cache)
            console.log('⚠️ Firestore unavailable or user not found, using Firebase Auth profile');
            userData = {
              ...buildUserFromFirebaseAuth(firebaseUser),
              lastLogin: new Date().toISOString(),
            };
            setDoc(doc(webDb, 'users', firebaseUser.uid), userData, { merge: true }).catch((err: any) => {
              console.warn('⚠️ Could not create user document in Firestore:', err);
            });
          }
        } catch (firestoreError: any) {
          // Handle Firestore timeout, offline/unavailable errors
          if (firestoreError.message === 'Firestore timeout' ||
              firestoreError.code === 'unavailable' || 
              firestoreError.code === 'failed-precondition' ||
              firestoreError.message?.includes('offline') ||
              firestoreError.message?.includes('Failed to get document') ||
              firestoreError.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
              firestoreError.message?.includes('Failed to fetch')) {
            console.warn('⚠️ Firestore timeout or unavailable. Using Firebase Auth profile.');
            userData = {
              ...buildUserFromFirebaseAuth(firebaseUser),
              lastLogin: new Date().toISOString(),
            };
            setDoc(doc(webDb, 'users', firebaseUser.uid), userData, { merge: true }).catch((err: any) => {
              console.warn('⚠️ Could not sync user to Firestore:', err);
            });
          } else {
            // Re-throw other errors (not timeout/unavailable)
            throw firestoreError;
          }
        }
        
        const finalized = await finalizeWebUserProfile(
          await applyBusinessOwnerTypeFromFirestore(
            { ...userData, id: firebaseUser.uid, lastLogin: new Date().toISOString() },
            firebaseUser.uid
          ),
          firebaseUser
        );
        console.log('✅ UnifiedAuthService: Login complete —', finalized.email, finalized.userType);
        return finalized;
      } else {
        // Mobile implementation
        if (!mobileAuth || !mobileFirestore) {
          initializeFirebase();
        }
        
        // Dynamic require for mobile (prevents webpack from bundling)
        if (!mobileAuth || !mobileFirestore) {
          throw new Error('React Native Firebase packages not installed. Install @react-native-firebase/auth and @react-native-firebase/firestore');
        }
        
        console.log('🔐 Signing in with Firebase Auth (mobile)...');
        const { signInWithEmailAndPassword } = require('@react-native-firebase/auth');
        const userCredential = await signInWithEmailAndPassword(mobileAuth, email, password);
        
        const firebaseUser = userCredential.user;
        console.log('✅ Firebase Auth sign-in successful:', firebaseUser.uid);
        
        // Get user data from Firestore
        console.log('🔐 Getting user data from Firestore (mobile)...');
        const { doc, getDoc, setDoc } = require('@react-native-firebase/firestore');
        const userDoc = await getDoc(doc(mobileFirestore, 'users', firebaseUser.uid));
        
        let userData: User;
        if (!userDoc.exists()) {
          // User exists in Auth but not Firestore (e.g. registration failed partway)
          // Create document from Auth data so user can log in
          console.log('⚠️ User document not found in Firestore, creating from Auth data...');
          userData = sanitizeForFirestore({
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
            phone: '',
            userType: 'customer',
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
            username: firebaseUser.email || '',
          }) as User;
          try {
            await setDoc(doc(mobileFirestore, 'users', firebaseUser.uid), userData, { merge: true });
            console.log('✅ Created missing user document in Firestore');
          } catch (createErr: any) {
            console.warn('⚠️ Could not create Firestore doc:', createErr?.message);
            // Still allow login - return Auth-based userData
          }
        } else {
          userData = userDoc.data() as User;
          console.log('✅ User data retrieved:', userData.email);
          
          // Update last login
          const { updateDoc } = require('@react-native-firebase/firestore');
          await updateDoc(doc(mobileFirestore, 'users', firebaseUser.uid), {
            lastLogin: new Date().toISOString(),
          });
        }
        
        return { ...userData, lastLogin: new Date().toISOString() };
      }
    } catch (error: any) {
      console.error('❌ Login error:', error);
      
      // Handle specific Firebase errors
      let errorMessage = 'Login failed';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * Sign in (or, for a first-time Google account, implicitly register) with
   * Google. Reuses the same Firestore profile lookup/create pipeline as
   * loginUser's "document doesn't exist yet" fallback — a first-time Google
   * sign-in gets a users/{uid} doc built from the Google profile the same
   * way a Firebase-Auth-but-no-Firestore-doc user does today.
   */
  static async loginWithGoogle(): Promise<{ user: User; isNewUser: boolean }> {
    if (Platform.OS === 'web') {
      if (!webAuth || !webDb) {
        initializeFirebase();
      }
      const { GoogleAuthProvider, signInWithPopup } = require('firebase/auth');
      const { doc, getDoc, setDoc } = require('firebase/firestore');

      console.log('🔐 Signing in with Google (web popup)...');
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(webAuth, provider);
      const firebaseUser = userCredential.user;
      console.log('✅ Google sign-in successful:', firebaseUser.uid);

      await clearAllAuthSessionCache();

      let userData: User;
      const userDoc = await getDoc(doc(webDb, 'users', firebaseUser.uid));
      const isNewUser = !userDoc.exists();
      if (!isNewUser) {
        userData = userDoc.data() as User;
        setDoc(doc(webDb, 'users', firebaseUser.uid), { lastLogin: new Date().toISOString() }, { merge: true }).catch(() => {});
      } else {
        console.log('🔐 First-time Google sign-in, creating user profile...');
        userData = sanitizeForFirestore(buildUserFromFirebaseAuth(firebaseUser)) as User;
        await setDoc(doc(webDb, 'users', firebaseUser.uid), userData, { merge: true });
      }

      const finalized = await finalizeWebUserProfile(
        await applyBusinessOwnerTypeFromFirestore(
          { ...userData, id: firebaseUser.uid, lastLogin: new Date().toISOString() },
          firebaseUser.uid
        ),
        firebaseUser
      );
      console.log('✅ UnifiedAuthService: Google login complete —', finalized.email, finalized.userType);
      return { user: finalized, isNewUser };
    }

    // Mobile: caller already obtained a Google ID token via
    // @react-native-google-signin/google-signin (see googleSignInService.ts)
    // and exchanges it for a Firebase credential here.
    throw new Error('loginWithGoogle() on native must be called via googleSignInService, not directly.');
  }

  /** Mobile-only: exchange a Google ID token for a Firebase session, same profile pipeline as web. */
  static async loginWithGoogleCredential(idToken: string): Promise<{ user: User; isNewUser: boolean }> {
    if (Platform.OS === 'web') {
      throw new Error('loginWithGoogleCredential is native-only; use loginWithGoogle on web.');
    }
    if (!mobileAuth || !mobileFirestore) {
      initializeFirebase();
    }
    if (!mobileAuth || !mobileFirestore) {
      throw new Error('React Native Firebase packages not installed.');
    }

    const { GoogleAuthProvider, signInWithCredential } = require('@react-native-firebase/auth');
    const credential = GoogleAuthProvider.credential(idToken);
    const userCredential = await signInWithCredential(mobileAuth, credential);
    const firebaseUser = userCredential.user;
    console.log('✅ Google sign-in successful (native):', firebaseUser.uid);

    const { doc, getDoc, setDoc, updateDoc } = require('@react-native-firebase/firestore');
    const userDoc = await getDoc(doc(mobileFirestore, 'users', firebaseUser.uid));
    const isNewUser = !userDoc.exists();

    let userData: User;
    if (isNewUser) {
      console.log('🔐 First-time Google sign-in, creating user profile...');
      userData = sanitizeForFirestore(buildUserFromFirebaseAuth(firebaseUser)) as User;
      await setDoc(doc(mobileFirestore, 'users', firebaseUser.uid), userData, { merge: true });
    } else {
      userData = userDoc.data() as User;
      await updateDoc(doc(mobileFirestore, 'users', firebaseUser.uid), { lastLogin: new Date().toISOString() });
    }

    return { user: { ...userData, lastLogin: new Date().toISOString() }, isNewUser };
  }

  // Send password reset email
  static async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      console.log('🔐 UnifiedAuthService: Sending password reset email...');
      
      if (Platform.OS === 'web') {
        if (!webAuth) {
          initializeFirebase();
        }
        
        if (!webAuth) {
          throw new Error('Firebase Auth not initialized. Check Firebase configuration.');
        }
        
        const { sendPasswordResetEmail } = require('firebase/auth');
        await sendPasswordResetEmail(webAuth, email);
        console.log('✅ Password reset email sent successfully (web)');
      } else {
        if (!mobileAuth) {
          initializeFirebase();
        }
        
        if (!mobileAuth) {
          throw new Error('React Native Firebase packages not installed');
        }
        
        const { sendPasswordResetEmail } = require('@react-native-firebase/auth');
        await sendPasswordResetEmail(mobileAuth, email);
        console.log('✅ Password reset email sent successfully (mobile)');
      }
    } catch (error: any) {
      console.error('❌ Password reset error:', error);
      
      // Handle specific Firebase errors
      let errorMessage = 'Failed to send password reset email';
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many requests. Please try again later';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      throw new Error(errorMessage);
    }
  }
  
  // Logout user
  static async logoutUser(): Promise<void> {
    try {
      console.log('🔐 UnifiedAuthService: Logging out...');
      
      if (Platform.OS === 'web') {
        if (!webAuth) {
          initializeFirebase();
        }
        const { signOut } = require('firebase/auth');
        await signOut(webAuth);
        await clearAllAuthSessionCache();
      } else {
        if (!mobileAuth) {
          initializeFirebase();
        }
        if (!mobileAuth) {
          throw new Error('React Native Firebase packages not installed');
        }
        const { signOut } = require('@react-native-firebase/auth');
        await signOut(mobileAuth);
        await clearAllAuthSessionCache();
      }
      
      console.log('✅ User logged out successfully');
    } catch (error: any) {
      console.error('❌ Logout error:', error);
      throw new Error(error.message || 'Logout failed');
    }
  }

  /**
   * Permanently delete the current user's account (Auth, Firestore, and local data).
   * Required for App Store Guideline 5.1.1(v) - Data Collection and Storage.
   */
  static async deleteAccount(): Promise<void> {
    try {
      let firebaseUser: any = null;
      let authInstance: any = null;
      let db: any = null;

      if (Platform.OS === 'web') {
        if (!webAuth || !webDb) {
          initializeFirebase();
        }
        firebaseUser = webAuth?.currentUser;
        authInstance = webAuth;
        db = webDb;
      } else {
        if (!mobileAuth) {
          initializeFirebase();
        }
        if (mobileAuth) {
          firebaseUser = mobileAuth.currentUser;
          authInstance = mobileAuth;
          const { getApp } = require('@react-native-firebase/app');
          const { getFirestore } = require('@react-native-firebase/firestore');
          db = getFirestore(getApp());
        }
      }

      const uid = firebaseUser?.uid;
      if (uid && db) {
        // 1. Delete Firestore data first (while still authenticated)
        try {
          if (Platform.OS === 'web') {
            const { doc, deleteDoc } = require('firebase/firestore');
            await deleteDoc(doc(db, 'users', uid));
            try {
              await deleteDoc(doc(db, 'businesses', uid));
            } catch (_) {
              // businesses doc may not exist for customers
            }
          } else {
            const { doc, deleteDoc } = require('@react-native-firebase/firestore');
            await deleteDoc(doc(db, 'users', uid));
            try {
              await deleteDoc(doc(db, 'businesses', uid));
            } catch (_) {
              // businesses doc may not exist for customers
            }
          }
        } catch (firestoreError: any) {
          console.warn('⚠️ DeleteAccount: Firestore delete error (continuing):', firestoreError?.message);
          // Continue to delete Auth and local so user is still removed from app
        }

        // 2. Delete Firebase Auth user
        if (firebaseUser && authInstance) {
          if (Platform.OS === 'web') {
            const { deleteUser } = require('firebase/auth');
            await deleteUser(firebaseUser);
          } else {
            await firebaseUser.delete();
          }
        }
      }

      // 3. Clear all local auth data (user, business_owner)
      try {
        const ProductionStorage = require('../utils/productionStorage').default;
        await ProductionStorage.multiRemove(['user', 'business_owner']);
      } catch (e) {
        console.warn('ProductionStorage clear error:', e);
      }
      try {
        const AuthManager = require('../utils/authManager').AuthManager;
        await AuthManager.clearAllAuth();
      } catch (e) {
        console.warn('AuthManager clear error:', e);
      }
      try {
        const SimpleStorage = require('../utils/simpleStorage').default;
        await SimpleStorage.removeItem('currentUser');
      } catch (e) {
        console.warn('SimpleStorage clear error:', e);
      }

      console.log('✅ Account deleted successfully');
    } catch (error: any) {
      console.error('❌ Delete account error:', error);
      const msg = error?.message || 'Account deletion failed';
      throw new Error(msg);
    }
  }
  
  // Get current user
  static async getCurrentUser(): Promise<User | null> {
    try {
      if (Platform.OS === 'web') {
        if (!webAuth || !webDb) {
          initializeFirebase();
        }
        
        const firebaseUser = webAuth.currentUser;
        
        if (!firebaseUser) {
          return null;
        }

        const authUid = firebaseUser.uid;

        // Fast path: cached profile only when it matches the signed-in Firebase UID
        const cachedUser = await loadCachedUserForUid(authUid);
        if (cachedUser) {
          const aligned = await finalizeWebUserProfile(cachedUser, firebaseUser);
          if (
            !firebaseUser.email ||
            normalizeAuthEmail(aligned.email) === normalizeAuthEmail(firebaseUser.email)
          ) {
            return aligned;
          }
          console.warn('⚠️ UnifiedAuthService: Discarding stale profile cache (email still mismatched)');
          await clearAllAuthSessionCache();
        }
        
        const { doc, getDoc } = require('firebase/firestore');
        
        try {
          const timeoutPromise = new Promise<null>((resolve) => 
            setTimeout(() => {
              console.warn('⚠️ UnifiedAuthService: Firestore getDoc timeout, using Auth profile');
              resolve(null);
            }, FIRESTORE_GET_USER_TIMEOUT_MS)
          );
          
          const getDocPromise = getDoc(doc(webDb, 'users', authUid));
          const userDoc: any = await Promise.race([getDocPromise, timeoutPromise]);
          
          if (userDoc && typeof userDoc.exists === 'function' && userDoc.exists()) {
            const userData = { ...(userDoc.data() as User), id: authUid };
            console.log('✅ UnifiedAuthService: Loaded full user data from Firestore');
            
            try {
              await saveCachedUser(userData);
            } catch (syncError) {
              console.warn('⚠️ Could not sync user to local storage:', syncError);
            }
            
            return await finalizeWebUserProfile(userData, firebaseUser);
          }
          
          console.warn('⚠️ UnifiedAuthService: Firestore doc missing or timeout');
          const authProfile = buildUserFromFirebaseAuth(firebaseUser);
          try {
            await saveCachedUser(authProfile);
          } catch {
            /* ignore */
          }
          return authProfile;
        } catch (firestoreError: any) {
          if (firestoreError.code === 'unavailable' || 
              firestoreError.code === 'failed-precondition' ||
              firestoreError.message?.includes('offline') ||
              firestoreError.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
              firestoreError.message?.includes('Failed to fetch') ||
              firestoreError.message?.includes('Failed to get document')) {
            console.warn('⚠️ Firestore unavailable. Using Firebase Auth profile.');
            return buildUserFromFirebaseAuth(firebaseUser);
          }
          throw firestoreError;
        }
      } else {
        if (!mobileAuth || !mobileFirestore) {
          const initialized = initializeFirebase();
          if (!initialized || !mobileAuth || !mobileFirestore) {
            console.warn('⚠️ Firebase not initialized for mobile, returning null user');
            return null; // Mobile packages not installed or initialization failed
          }
        }
        
        try {
          const firebaseUser = mobileAuth.currentUser;
          if (!firebaseUser) {
            return null;
          }
        
          try {
            const { doc: docUser, getDoc: getDocUser } = require('@react-native-firebase/firestore');
            const userDoc = await getDocUser(docUser(mobileFirestore, 'users', firebaseUser.uid));
            
            if (!userDoc.exists()) {
              return null;
            }
            
            return userDoc.data() as User;
          } catch (firestoreError: any) {
          // Handle Firestore errors (e.g., network issues)
          if (firestoreError.code === 'unavailable' || 
              firestoreError.message?.includes('Failed to fetch')) {
            console.warn('⚠️ Firestore unavailable. Using Auth user data only.');
            // Return minimal user data from Auth when Firestore is unavailable
            return {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || firebaseUser.email || 'User',
              phone: '',
              userType: 'customer', // Default fallback
              createdAt: new Date().toISOString(),
              username: firebaseUser.email || '',
            };
          }
            throw firestoreError; // Re-throw other errors
          }
        } catch (authError: any) {
          console.error('❌ Error accessing Firebase Auth:', authError);
          return null;
        }
      }
    } catch (error: any) {
      console.error('❌ Error getting current user:', error);
      // Don't log ERR_BLOCKED_BY_CLIENT errors as they're typically ad blocker related
      if (!error.message?.includes('ERR_BLOCKED_BY_CLIENT')) {
        console.error('Error details:', error.message || error);
      }
      return null;
    }
  }
  
  // Listen to auth state changes
  static onAuthStateChanged(callback: (user: User | null) => void): () => void {
    if (Platform.OS === 'web') {
      if (!webAuth) {
        initializeFirebase();
      }
      
      // Check if webAuth is still null after initialization
      if (!webAuth) {
        console.error('❌ Firebase Auth not initialized for web. Check Firebase configuration.');
        console.error('💡 Make sure .env file has REACT_APP_FIREBASE_* variables set');
        // Return no-op unsubscribe function
        return () => {};
      }
      
      const { onAuthStateChanged: webOnAuthStateChanged } = require('firebase/auth');
      return webOnAuthStateChanged(webAuth, async (firebaseUser: any) => {
        try {
          if (firebaseUser) {
            const user = await this.getCurrentUser();
            callback(user);
          } else {
            callback(null);
          }
        } catch (error: any) {
          // Handle errors gracefully - if Firestore is blocked, still return Auth user
          if (error.message?.includes('ERR_BLOCKED_BY_CLIENT') || 
              error.code === 'unavailable') {
            console.warn('⚠️ Firestore blocked or unavailable. Using Auth data only.');
            if (firebaseUser) {
              callback(buildUserFromFirebaseAuth(firebaseUser));
            } else {
              callback(null);
            }
          } else {
            console.error('Error in auth state change:', error);
            callback(null);
          }
        }
      });
    } else {
      if (!mobileAuth) {
        initializeFirebase();
      }
      
      if (!mobileAuth) {
        // Return no-op function if mobile packages not installed
        return () => {};
      }
      
      const { onAuthStateChanged: mobileOnAuthStateChanged } = require('@react-native-firebase/auth');
      return mobileOnAuthStateChanged(mobileAuth, async (firebaseUser: any) => {
        try {
          if (firebaseUser) {
            const user = await this.getCurrentUser();
            callback(user);
          } else {
            callback(null);
          }
        } catch (error: any) {
          // Handle errors gracefully
          console.error('Error in auth state change:', error);
          callback(null);
        }
      });
    }
  }
  
  // Check if email exists
  static async emailExists(email: string): Promise<boolean> {
    try {
      if (Platform.OS === 'web') {
        if (!webDb) {
          initializeFirebase();
        }
        
        const { collection, query, where, getDocs } = require('firebase/firestore');
        const q = query(
          collection(webDb, 'users'),
          where('email', '==', email.toLowerCase())
        );
        const snapshot = await getDocs(q);
        return !snapshot.empty;
      } else {
        if (!mobileFirestore) {
          initializeFirebase();
        }
        
        if (!mobileFirestore) {
          return false; // Mobile packages not installed
        }
        
        const { collection, query, where, getDocs } = require('@react-native-firebase/firestore');
        const q = query(collection(mobileFirestore, 'users'), where('email', '==', email.toLowerCase()));
        const snapshot = await getDocs(q);
        return !snapshot.empty;
      }
    } catch (error) {
      console.error('❌ Error checking email:', error);
      return false;
    }
  }
}

